import { MenuList, MenuItem } from '@mui/material';
import React from 'react';
import {
	InvoiceViewPaymentInstallmentsSubMenuPaymentInstallmentsFragment,
	InstallmentStatus,
	InvoiceViewPaymentInstallmentsSubMenuInvoiceFragment,
} from '#types';
import { gql, DocumentNode } from '@apollo/client';
import { InvoiceViewPaymentInstallmentDetailsModalProps } from '../InvoiceViewPaymentInstallmentDetailsModal';
import rpcShared from '@rockpapercoin/rpc-shared';

const invoiceViewPaymentInstallmentsSubMenuPaymentInstallmentsFragment = gql`
	fragment invoiceViewPaymentInstallmentsSubMenuPaymentInstallments on PaymentInstallment {
		id
		status
	}
`;

const invoiceViewPaymentInstallmentsSubMenuInvoiceFragment = gql`
	fragment invoiceViewPaymentInstallmentsSubMenuInvoice on Invoice {
		id
		organization {
			...getViewingUserDocumentRelationshipOrganization
		}
	}
	${ rpcShared.organizations
		.getViewingUserDocumentRelationshipOrganizationFragment }
`;

export type SubMenuProps = {
	invoice?: InvoiceViewPaymentInstallmentsSubMenuInvoiceFragment;
	requestingUserBaseId?: string;
	handleClose: () => void;
	installment: InvoiceViewPaymentInstallmentsSubMenuPaymentInstallmentsFragment;
	onViewDetails: (
		paymentInstallment: InvoiceViewPaymentInstallmentDetailsModalProps['paymentInstallment']
	) => void;
	onRefund: (
		paymentInstallment: InvoiceViewPaymentInstallmentDetailsModalProps['paymentInstallment']
	) => void;
	onRecordOrUpdate: (
		paymentInstallment: InvoiceViewPaymentInstallmentDetailsModalProps['paymentInstallment']
	) => void;
};

type SubMenuPropsWithFragment = React.FC<SubMenuProps> & {
	fragments: {
		invoiceViewPaymentInstallmentsSubMenuPaymentInstallmentsFragment: DocumentNode;
		invoiceViewPaymentInstallmentsSubMenuInvoiceFragment: DocumentNode;
	};
};

export const SubMenu: SubMenuPropsWithFragment = ( {
	invoice,
	requestingUserBaseId,
	handleClose,
	installment,
	onViewDetails,
	onRefund,
	onRecordOrUpdate,
} ) => {
	if ( !installment.status ) {
		return null;
	}
	const actionableInstallmentStatusesForRefund = [
		InstallmentStatus.FullyPaid,
		InstallmentStatus.FullyPaidPartiallyRefunded,
		InstallmentStatus.Refunded,
	];

	const { viewingUserIsAdminOrManager: viewingUserIsVendorAdminOrManager } =
		rpcShared.organizations.getViewingUserDocumentRelationship( {
			organization: invoice?.organization,
			viewingUserId: requestingUserBaseId,
		} );

	const shouldRenderRefundOptions =
		viewingUserIsVendorAdminOrManager &&
		actionableInstallmentStatusesForRefund.includes( installment.status );

	return (
		<MenuList data-cy='installment-submenu'>
			<MenuItem
				key='details'
				data-cy='installment-submenu-details'
				autoFocus
				onClick={ () => {
					handleClose();
					onViewDetails( installment );
				} }
			>
				<span>View details</span>
			</MenuItem>
			{ viewingUserIsVendorAdminOrManager
				? [
					<MenuItem
						key='payment'
						data-cy='installment-submenu-payment'
						onClick={ () => {
							handleClose();
							onRecordOrUpdate( installment );
						} }
					>
						<span>
							{ installment.status === InstallmentStatus.Unpaid
								? 'Record payment'
								: 'Update payment' }
						</span>
					</MenuItem>,
					shouldRenderRefundOptions ? (
						<MenuItem
							key='refund'
							data-cy='installment-submenu-refund'
							onClick={ () => {
								handleClose();
								onRefund( installment );
							} }
						>
							<span>
								{ installment.status === InstallmentStatus.FullyPaid
									? 'Refund payment'
									: 'Update refund' }
							</span>
						</MenuItem>
					) : null,
				  ]
				: null }
		</MenuList>
	);
};

SubMenu.fragments = {
	invoiceViewPaymentInstallmentsSubMenuPaymentInstallmentsFragment,
	invoiceViewPaymentInstallmentsSubMenuInvoiceFragment,
};
